import { useCallback, useEffect, useRef, useState } from 'react'

import { useAuth } from '~/auth/auth-context'
import { Spinner } from '~/components/working'
import { useBeat } from '~/components/use-work'
import { LogoMark } from '~/components/icons'
import { Skeleton } from '~/components/skeleton'
import { SharedTaste } from '~/components/shared-taste'
import { config } from '~/config/env'

interface GoogleButtonOptions {
  theme: string
  size: string
  shape?: string
  text?: string
  width?: number
  logo_alignment?: string
  locale?: string
}

interface GoogleIdentityServices {
  accounts: {
    id: {
      initialize: (options: {
        client_id: string
        callback: (response: { credential: string }) => void
      }) => void
      renderButton: (parent: HTMLElement, options: GoogleButtonOptions) => void
    }
  }
}

declare global {
  interface Window {
    google?: GoogleIdentityServices
  }
}

const GIS_SRC = 'https://accounts.google.com/gsi/client?hl=en'

const welcomeGroups = [
  [
    ['strength-0.webp', 'Barbell back squat'],
    ['strength-1.webp', 'Bench press'],
    ['strength-2.webp', 'Deadlift'],
    ['strength-3.webp', 'Pull-up'],
  ],
  [
    ['calisthenics-0.webp', 'Handstand'],
    ['calisthenics-1.webp', 'Ring dip'],
    ['calisthenics-2.webp', 'Bar muscle-up'],
    ['calisthenics-3.webp', 'Front lever'],
  ],
  [
    ['moments-0.webp', 'Recording a workout'],
    ['moments-1.webp', 'Chalked barbell grip'],
    ['moments-2.webp', 'Kettlebell swing'],
    ['moments-3.webp', 'Training partners celebrating'],
  ],
] as const

function GoogleMark({ className }: { className?: string }) {
  return (
    <svg className={className} aria-hidden viewBox="0 0 24 24">
      <path
        fill="#4285F4"
        d="M21.6 12.23c0-.71-.06-1.39-.18-2.05H12v3.87h5.38a4.6 4.6 0 0 1-2 3.02v2.51h3.24c1.9-1.75 2.98-4.33 2.98-7.35Z"
      />
      <path
        fill="#34A853"
        d="M12 22c2.7 0 4.98-.9 6.63-2.42l-3.25-2.51c-.9.6-2.05.96-3.38.96-2.61 0-4.82-1.76-5.61-4.13H3.04v2.6A10 10 0 0 0 12 22Z"
      />
      <path
        fill="#FBBC05"
        d="M6.39 13.9A6.02 6.02 0 0 1 6.07 12c0-.66.11-1.3.32-1.9V7.5H3.04A10 10 0 0 0 2 12c0 1.61.38 3.14 1.04 4.5l3.35-2.6Z"
      />
      <path
        fill="#EA4335"
        d="M12 5.97c1.47 0 2.79.5 3.83 1.5L18.7 4.6A9.64 9.64 0 0 0 12 2a10 10 0 0 0-8.96 5.5l3.35 2.6C7.18 7.73 9.39 5.97 12 5.97Z"
      />
    </svg>
  )
}

function WelcomeGallery() {
  // The three groups ride one belt, and the first rides again at its tail: the moment the
  // belt restarts, that fourth panel is standing exactly where the first began, so the loop
  // closes without a seam. The tail is scenery rather than content — assistive tech already
  // has all twelve moments — so it is hidden from the tree.
  const belt = [...welcomeGroups, welcomeGroups[0]]
  return (
    <section
      aria-label="Welcome fitness gallery"
      className="welcome-gallery pointer-events-none absolute inset-x-0 top-0 h-[58dvh] min-h-96 overflow-hidden"
    >
      <div className="welcome-belt">
        {belt.map((group, panelIndex) => (
          <div
            key={`${group[0][0]}-${String(panelIndex)}`}
            {...(panelIndex === welcomeGroups.length
              ? { 'aria-hidden': true }
              : {
                  role: 'group',
                  'aria-label': `Fitness moments ${String(panelIndex * 4 + 1)} through ${String(panelIndex * 4 + 4)}`,
                })}
            className="welcome-gallery-group"
          >
            {group.map(([src, alt], slot) => (
              <img
                key={src}
                src={`/welcome/${src}`}
                alt={alt}
                className="welcome-gallery-tile absolute object-cover"
                data-slot={slot}
              />
            ))}
          </div>
        ))}
      </div>
      <div aria-hidden className="welcome-gallery-fade absolute inset-0" />
    </section>
  )
}

/** The one external script in the app, loaded only here. */
function loadGoogleIdentity(): Promise<GoogleIdentityServices> {
  return new Promise((resolve, reject) => {
    if (window.google) {
      resolve(window.google)
      return
    }
    const existing = document.querySelector(`script[src="${GIS_SRC}"]`)
    const script = existing instanceof HTMLScriptElement ? existing : null
    const element = script ?? document.createElement('script')
    element.addEventListener('load', () => {
      if (window.google) {
        resolve(window.google)
      } else {
        reject(new Error('Google Identity Services did not load'))
      }
    })
    element.addEventListener('error', () => {
      reject(new Error('Google Identity Services failed to load'))
    })
    if (!script) {
      element.src = GIS_SRC
      element.async = true
      document.head.append(element)
    }
  })
}

/**
 * The welcome. Until sign-in succeeds this screen is the whole app, so it is the mark, what
 * the app is for, and one way in: the mark and the words in the middle of the page, the way
 * in waiting under them.
 */
export function SignInScreen({ taste }: { taste?: string | undefined } = {}) {
  const { signInWithGoogle } = useAuth()
  const buttonHost = useRef<HTMLDivElement>(null)
  const [failure, setFailure] = useState<string | null>(null)
  /** True once Google's own button is on the page. Until then its room is held. */
  const [rendered, setRendered] = useState(false)
  /** True once Google's own button could not be put on the page, so we offer our own. */
  const [unrendered, setUnrendered] = useState(false)
  const [signing, setSigning] = useState(false)
  const signingShown = useBeat(signing)
  /** Bumped to ask for Google's button again after it could not be fetched. */
  const [attempt, setAttempt] = useState(0)

  const onCredential = useCallback(
    (credential: string) => {
      setFailure(null)
      setSigning(true)
      signInWithGoogle(credential).catch(() => {
        setSigning(false)
        setFailure('Google sign-in failed. Try again.')
      })
    },
    [signInWithGoogle],
  )

  useEffect(() => {
    const clientId = config.googleClientId
    if (clientId === null) {
      return
    }
    let cancelled = false
    loadGoogleIdentity()
      .then((google) => {
        if (cancelled || !buttonHost.current) {
          return
        }
        google.accounts.id.initialize({
          client_id: clientId,
          callback: (response) => {
            onCredential(response.credential)
          },
        })
        // Google's own outlined button, matching the same control in the Android welcome.
        google.accounts.id.renderButton(buttonHost.current, {
          theme: 'outline',
          size: 'large',
          shape: 'rectangular',
          text: 'continue_with',
          logo_alignment: 'left',
          locale: 'en',
          width: 320,
        })
        setRendered(true)
      })
      .catch(() => {
        if (!cancelled) {
          setUnrendered(true)
          setFailure('Could not reach Google sign-in. Check the connection and try again.')
        }
      })
    return () => {
      cancelled = true
    }
  }, [onCredential, attempt])

  return (
    <main className="welcome-screen relative flex min-h-dvh flex-col items-center justify-between overflow-hidden bg-surface px-6 py-10 text-center text-ink sm:py-14">
      <WelcomeGallery />

      <div aria-hidden className="min-h-[42dvh] flex-1" />

      {/*
        Arriving on a link, the page is about what was handed over rather than about the app —
        the nutrition spec, Opening a link. Enough of it to be worth signing in for, and the
        app's own welcome where there is no link to say anything about.
      */}
      <div className="relative z-10 flex flex-col items-center gap-5">
        {taste === undefined ? (
          <>
            <LogoMark className="h-16 w-auto text-ink drop-shadow-2xl sm:h-20" />
            <h1 className="welcome-heading text-4xl font-semibold tracking-[-0.045em] text-balance sm:text-[3.25rem] sm:leading-[1.04]">
              Every set, remembered.
            </h1>
            <p className="max-w-sm text-base text-ink-muted text-balance">
              Your lifts, your PRs and the videos of them, kept the way photos are.
            </p>
          </>
        ) : (
          <SharedTaste payload={taste} />
        )}
      </div>

      <div className="relative z-10 flex flex-1 flex-col items-center justify-end gap-4 pt-8">
        {config.googleClientId === null ? (
          <p className="max-w-sm text-sm text-ink-muted">
            Sign-in isn&apos;t configured yet. Set <code>VITE_GOOGLE_CLIENT_ID</code> here and{' '}
            <code>GOOGLE_CLIENT_ID</code> on the server — see <code>apps/server/README.md</code>.
          </p>
        ) : signing ? (
          // Working is said on the control itself, in the room it already took.
          // Google's own button cannot be made to wait, so it gives way to one of the same
          // size that can, working in its place — the app spec, A press that waits.
          <button
            type="button"
            aria-busy
            aria-label="Signing in"
            className="flex h-14 w-80 items-center justify-center rounded-2xl border border-[#dadce0] bg-white font-semibold text-[#3c4043]"
          >
            {signingShown ? <Spinner /> : null}
          </button>
        ) : (
          <>
            <div ref={buttonHost} className={rendered ? undefined : 'hidden'} />
            {rendered ? null : unrendered ? (
              <button
                type="button"
                className="flex h-14 w-80 items-center justify-center gap-3 rounded-2xl border border-[#dadce0] bg-white font-semibold text-[#3c4043] transition-colors hover:bg-[#f8f9fa]"
                onClick={() => {
                  setFailure(null)
                  setUnrendered(false)
                  setAttempt((held) => held + 1)
                }}
              >
                <GoogleMark className="size-6" />
                Continue with Google
              </button>
            ) : (
              // Google's button is on its way; the room it will take is held meanwhile.
              <Skeleton className="h-14 w-80 rounded-2xl" />
            )}
          </>
        )}
        {failure !== null && <p className="text-sm text-danger">{failure}</p>}
      </div>
    </main>
  )
}
